import type { LinksFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ClientLoaderFunctionArgs, ShouldRevalidateFunction } from '@remix-run/react';
import * as Sentry from '@sentry/remix';

import Document from '~/components/Document';
import remixI18n from '~/i18next.server';
import { loaderSession } from '~/session';

import getSite from './services/api/sites/getSite.server';
import getWebTheme from './services/api/web_theme/getWebTheme.server';

import App from './components/App';
import { getAppGlobals } from './components/AppGlobals/utils';
import ErrorBoundary from './components/ErrorBoundary';

import getCookieValue from './utils/getCookieValue';
import { handleRedirect } from './utils/handleRedirect';
import isUserAgentMobile from './utils/isUserAgentMobile';
import redirectWithToast from './utils/redirectWithToast/index.server';
import { setCookieToken } from './utils/setTokenCookie/setCookieToken';
import { shouldRevalidateLoaderData } from './utils/shouldRevalidateLoaderData';

import type { Admin } from './interfaces/admin';
import type { Publication } from './interfaces/publication';
import type { Site } from './interfaces/site';
import type { Subscription } from './interfaces/subscriptions';

import { env } from './env';
import type { ScriptRegistration } from './global';

declare global {
  interface Window {
    beehiiv_registerScript: (script: string) => void;
    beehiiv_scriptsAfterHydration: Map<string /* url */, Map<string /* id/hash */, ScriptRegistration>>;
  }
}

const appGlobals = getAppGlobals();

export const links: LinksFunction = () => {
  return [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
  ];
};

export const handle = {
  // In the handle export, we could add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  i18n: ['common'],
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.publication || !data?.site) {
    return [
      { title: 'beehiiv' },
      { description: 'The newsletter platform built for growth.' },
      { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
    ];
  }

  const { publication, site } = data;
  const { tags, email_sender_name, logo, rss_feeds } = publication;
  const keywords = tags.map((tag: { name: string }) => tag.name).join(', ');

  const settingsMetadata = site?.viewable_site_version?.metadata;

  const metaData: Array<Record<string, string>> = [
    { title: settingsMetadata?.meta_title || data?.publication?.name || '' },
    { description: settingsMetadata?.meta_description || data?.publication?.description || '' },
    { keywords },
    { author: email_sender_name || '' },
    {
      tagName: 'link',
      rel: 'icon',
      href: logo?.thumb?.url || '',
      as: 'image',
    },
  ];

  if (settingsMetadata?.noindex_enabled) {
    metaData.push({ robots: 'noindex' });
  }

  if (rss_feeds?.length) {
    metaData.push({
      tagName: 'link',
      rel: 'alternate',
      type: 'application/rss+xml',
      title: `${publication.name} RSS Feed`,
      href: rss_feeds[0].url || '',
    });
  }

  return metaData;
};

export interface RootLoaderData {
  site: Site;
  publication: Publication;
  recommendingPublication: Pick<Publication, 'id' | 'name'>;
  rootStyles: string;
  isLoggedIn: boolean;
  isFetchingSubscription: boolean;
  subscription: Subscription;
  admin: Admin;
  isProdEnv: boolean;
  facebookAppId: string;
  currentPath: string;
  showLoggedInToast: boolean;
  isStagingPreview: boolean;
  SENTRY_RELEASE: string;
  isUAStringMobile: boolean;
  isPostsPage: boolean;
  noScript: boolean;
}

export const loader: LoaderFunction = async ({ request }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const searchParams = url.searchParams;

  // If the request has cache buster, remove it and redirect without cache
  if (searchParams.has('__cb')) {
    searchParams.delete('__cb');
    throw redirect(`${url.pathname}${url.search}`, {
      headers: { 'cache-control': 'no-store' },
    });
  }
  const noScript = searchParams.get('no_script') === 'true';

  const [site, webTheme] = await Promise.all([getSite(request, session), getWebTheme(request, session)]);
  const { publication } = site;

  if (site.site_redirects_enabled) {
    handleRedirect(request, site.site_redirects || []);
  }

  Sentry.setContext('publication', {
    id: publication.id,
    name: publication.name,
  });

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  if (url.searchParams.get('upgrade') === 'success') {
    return redirect(
      redirectWithToast({
        baseUrl: '/',
        toast: 'success',
        title: t('upgrade.success.title') || '',
        message: t('upgrade.success.message'),
      }),
    );
  }

  // Check if the request host is different than the custom domain configured
  //   for the current publication (if any). If it is, we will redirect to the
  //   custom domain, retaining any parameters. This is useful for redirecting
  //   beehiiv publication subdomains to custom domains.
  //
  //   Example: get mypublication.beehiiv.com/p/test?foo=bar
  //     => 301 https://blog.mypublication.com/p/test?foo=bar
  // Make sure to strip the port from the host name if present
  const hostName = (request.headers.get('x-forwarded-host') || url.hostname).split(':')[0];

  const configuredDomain = publication.configured_domain;
  if (configuredDomain && configuredDomain !== hostName) {
    url.protocol = 'https:'; // Ensure the protocol is https
    url.hostname = configuredDomain;
    throw redirect(url.href, {
      status: 301,
    });
  }
  const cookieHeader = request.headers.get('Cookie');
  const isStagingPreview = cookieHeader?.includes('v2-preview');

  // Upgrade page redirect: If user's paid subscriptions are disabled, redirect back to home with a toast
  const isUpgradePage = url.pathname?.startsWith('/upgrade');
  if (!publication?.premium_enabled && isUpgradePage) {
    return redirect(
      redirectWithToast({
        baseUrl: '/',
        toast: 'error',
        message: t('upgrade.error.message'),
      }),
      307,
    );
  }

  const userAgent = request.headers.get('user-agent') || '';
  const isUAStringMobile = isUserAgentMobile(userAgent);

  return json({
    site,
    // TODO: Remove publication from root loader data since it is in site object
    publication,
    isLoggedIn: false,
    isFetchingSubscription: true,
    rootStyles: webTheme?.root_styles,
    admin: null,
    subscription: null,
    isProdEnv: process.env.NODE_ENV === 'production',
    facebookAppId: env.FACEBOOK_APP_ID,
    currentPath: url.pathname,
    showLoggedInToast: false,
    hostName,
    isStagingPreview,
    // include latest release in root to compare with initial document request
    // in AppGlobals. If different, for a refresh
    SENTRY_RELEASE: appGlobals.SENTRY_RELEASE,
    isUAStringMobile,
    noScript,
  });
};

export const clientLoader = async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<RootLoaderData>();
  if (!serverData?.site || !serverData?.publication) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }
  try {
    const url = new URL(request.url);
    const { publication } = serverData;

    const hasToastParams = url.searchParams.get('toast') && url.searchParams.get('message');
    const showLoggedInToast = url.searchParams.get('jwt_token') && !hasToastParams;
    const token = url.searchParams.get('jwt_token') || getCookieValue('token', document.cookie);
    const adminToken = url.searchParams.get('admin_token') || getCookieValue('admin_token', document.cookie);
    const requests = [];

    if (token) {
      const requestBody: { jwt_token: string } = { jwt_token: token };

      requests.push(
        fetch('/current_subscription', {
          method: 'POST',
          headers: {
            'Sec-Fetch-Dest': 'empty',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }).then((res) => {
          if (res.ok) {
            document.cookie = setCookieToken({ key: 'token', token, isProdEnv: serverData?.isProdEnv });
          }
          return res;
        }),
      );
    } else {
      requests.push(Promise.resolve(null));
    }

    if (adminToken) {
      requests.push(
        fetch('/current_admin', {
          method: 'POST',
          headers: {
            'Sec-Fetch-Dest': 'empty',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ admin_token: adminToken }),
        }).then((res) => {
          if (res.ok) {
            document.cookie = setCookieToken({
              key: 'admin_token',
              token: adminToken,
              isProdEnv: serverData?.isProdEnv,
            });
          }
          return res;
        }),
      );
    } else {
      requests.push(Promise.resolve(null));
    }

    const [subscriptionRes, adminRes] = await Promise.all(requests);
    const [subscription, admin] = await Promise.all([
      subscriptionRes?.ok ? subscriptionRes.json() : null,
      adminRes?.ok ? adminRes.json() : null,
    ]);

    // PrivatePubs redirect: if publication is private or requires approval and user isn't subscribed
    const isPrivate = serverData.site?.publication?.private;
    const requireSubscriberApproval = serverData.site?.publication?.require_subscriber_approval;
    const shouldRedirect = !subscription && (isPrivate || requireSubscriberApproval);
    const isPostsPage = url.pathname?.startsWith('/p/');
    const isDraftPost = url.searchParams.get('draft') === 'true';

    if (shouldRedirect && isPostsPage && !isDraftPost) {
      window.location.href = '/';
      return serverData;
    }

    // Login page redirect: This specific redirect is for the /login route and checks if the user is already logged in
    const isUserLoggedIn = Boolean(subscription);
    const isLoginPage = url.pathname?.startsWith('/login');
    if (isUserLoggedIn && isLoginPage) {
      window.location.href = '/';
      return serverData;
    }

    if (subscription) {
      Sentry.setContext('subscription', {
        id: subscription.id,
        tier: subscription.tier,
        status: subscription.status,
        email: subscription.email,
        subscriberId: subscription.subscriber_id,
      });
    }

    if (!subscription && publication.require_subscriber_approval && window.location.pathname !== '/' && !isLoginPage) {
      window.location.href = '/';
    }

    // Remove any tokens and toast params from the url if they are present
    if (url.searchParams.has('jwt_token') || url.searchParams.has('admin_token') || url.searchParams.has('toast')) {
      url.searchParams.delete('jwt_token');
      url.searchParams.delete('admin_token');
      url.searchParams.delete('toast');
      url.searchParams.delete('message');
      url.searchParams.delete('title');
      url.searchParams.delete('toastId');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }

    return {
      ...serverData,
      isLoggedIn: !!subscription,
      admin: admin ?? null,
      isFetchingSubscription: false,
      subscription,
      showLoggedInToast: !!(showLoggedInToast && subscription),
      isPostsPage,
    };
  } catch (_error) {
    Sentry.captureException(_error);

    return {
      ...serverData,
      isLoggedIn: false,
      admin: null,
      subscription: null,
      isFetchingSubscription: false,
    };
  }
};

clientLoader.hydrate = true;

// Use Layout export to wrap both App and ErrorBoundary in Document
// https://v2.remix.run/docs/file-conventions/root#layout-export
export const Layout = ({ children }: { children: React.ReactNode }) => {
  return <Document>{children}</Document>;
};

const SentryApp = Sentry.withSentry(App);
// biome-ignore lint/style/useComponentExportOnlyModules: Sentry wrapped component export
export default SentryApp;

// Export default ErrorBoundary for Remix
// biome-ignore lint/style/useComponentExportOnlyModules: ErrorBoundary export for Remix
export { ErrorBoundary };

export const shouldRevalidate: ShouldRevalidateFunction = shouldRevalidateLoaderData;
